import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetContractsList } from '@/modules/workforce/Contracts/apiHooks';
import { useGetContractsColumns } from '@/modules/workforce/Contracts/components/Main/ContractsTable/logic';
import { TAB_QUERY_INDICATOR } from '@/modules/workforce/Contracts/components/Main/FiltersTabs';
import { useContractsContext } from '@/modules/workforce/Contracts/context';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { Table } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { EmptyCard } from '@/shared/components/molecules';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';

import FemaleFarmer from '~/images/shared/FemaleFarmer.svg';

function ContractsTable({
  suppliersCount,
}: {
  suppliersCount: number | undefined;
}) {
  const { t } = useTranslation('common');
  const { query, push } = useRouter();
  const [pageNumber, setPageNumber] = useState(1);
  const { contractsColumns } = useGetContractsColumns();
  const { openModal } = useModal();
  const {
    state: { keyword },
  } = useContractsContext();

  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;

  const { data } = useGetContractsList({
    pageSize: 10,
    entityId: selectedAsset?.id,
    pageNumber,
    ...(keyword && { search: keyword }),
    status: Number(query?.[TAB_QUERY_INDICATOR]),
  });

  //  TODO: need a better solution than useEffect because it causes unneccessary requests.
  useDidUpdateEffect(() => {
    setPageNumber(1);
  }, [selectedAsset?.id, query?.[TAB_QUERY_INDICATOR], keyword]);

  return data?.supplierAssignments?.length ? (
    <Table<IContracts.IContractsListSchema>
      selection={false}
      footerClassName='bg-neutralVariant-95'
      headCellClassName='text-start border-b-xs border-neutralVariant-90 text-surface-10 font-semi-bold'
      rowCellClassName='border-b-xs border-neutralVariant-90'
      headContainerClassName='!bg-background-neutral-99'
      tableWrapperClassName='!rounded-xl border-xs border-outline-variant'
      rowContainerClassName='cursor-pointer'
      columns={contractsColumns(t, selectedAsset?.currency ?? '')}
      onClickRow={(row) =>
        store?.isDemoFarm
          ? openModal(<DemoCTA />)
          : push({
              pathname: `${routes.contracts}/[id]`,
              query: {
                id: row.supplierAssignmentId,
              },
            })
      }
      data={data.supplierAssignments}
      pagination
      currentPage={pageNumber}
      totalCount={data.totalCount}
      onPageChange={(pageNumber) => setPageNumber(pageNumber)}
      perPage={10}
    />
  ) : (
    <EmptyCard
      message={t('no-contracts')}
      vector={<FemaleFarmer />}
      directiveMessage={!suppliersCount && t('no-contracts-no-suppliers')}
      variant='icon'
      fullSVG
      classeName='my-40 max-w-md mx-auto text-center'
    />
  );
}

export { ContractsTable };
